import { createTable, getCoreRowModel, getSortedRowModel } from '@tanstack/table-core';
import { TableLayoutScope } from './table-layout.scope';
import { COLUMN_WIDTH, TablePreferences } from './table-preferences';
/**
 * O campo da coluna de ACOES - a ultima, presa a direita, que so guarda botoes: nao
 * ordena, nao se estica e nao sai do lugar. O mesmo nome nas telas que a tem.
 */
export const ACTION_COLUMN = 'acao';
export class TableModel {
    get measure() {
        let measure = this.measures.get(this.measureSet);
        if (!measure) {
            measure = { natural: {}, withRows: false };
            this.measures.set(this.measureSet, measure);
        }
        return measure;
    }
    constructor(options) {
        /** O que a view entrega ao `DataTable`. Um so objeto pela vida da tela. */
        this.layout = new TableLayoutScope();
        this.specs = [];
        this.visible = [];
        /**
         * As larguras medidas, por CONJUNTO. Uma tabela que alterna de conteudo (a lista de
         * dias e as leituras de um dia, no historico) mede cada um uma vez, e voltar a um
         * conjunto ja medido nao passa de novo pelo layout automatico.
         */
        this.measures = new Map();
        this.measureSet = '';
        this.dirty = false;
        this.flushListener = () => this.flush();
        this.pinStart = options.pinStart ?? 0;
        this.preferences = new TablePreferences(options.name, options.account, options.legacy);
        this.engine = createTable({
            data: [],
            columns: [],
            state: {},
            onStateChange: () => undefined,
            renderFallbackValue: null,
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: getSortedRowModel(),
            manualPagination: true,
            enableSortingRemoval: false
        });
        this.layout.onMeasure = (widths, withRows) => {
            // A PRIMEIRA medida com dado vale para todas as colunas: a anterior, se houve,
            // era so a do cabecalho. Depois dela, medir de novo (apareceu uma coluna) so
            // preenche quem ainda nao tem largura - senao o conteudo da pagina da vez
            // redefiniria a largura de todas, e a promessa e de que ela nao muda.
            const measure = this.measure;
            const fresh = withRows && !measure.withRows;
            const next = { ...measure.natural };
            for (const [field, width] of Object.entries(widths)) {
                if (fresh || next[field] === undefined)
                    next[field] = width;
            }
            measure.natural = next;
            if (withRows)
                measure.withRows = true;
            this.markDirty();
        };
        this.layout.onResize = (field, width) => {
            this.preferences.resize(field, width);
            this.markDirty();
        };
    }
    /**
     * Liga o modelo a quem hospeda a tabela, e devolve o scope que a view desenha.
     * Chamado em toda viagem; o registro no gerenciador do host acontece uma vez.
     */
    show(host) {
        if (host !== this.host) {
            this.host?.updateManager.removeOnBeforeScopeUpdateListener(this.flushListener);
            host.updateManager.addOnBeforeScopeUpdateListener(this.flushListener);
            this.host = host;
        }
        this.layout.update = host.update;
        this.markDirty();
        return this.layout;
    }
    /**
     * Os campos na ordem que o leitor arrastou. As `fixed.start` primeiras e as
     * `fixed.end` ultimas nao saem do lugar - a coluna que identifica a linha e a de
     * acoes -, e so o meio segue a escolha guardada.
     */
    arrange(fields, fixed = {}) {
        const [head, middle, tail] = split(fields, fixed);
        return [...head, ...this.preferences.arrange(middle), ...tail];
    }
    /** Uma coluna solta sobre outra, entre as que se movem. Devolve se a ordem mudou. */
    move(fields, source, target, fixed = {}) {
        const [, middle] = split(fields, fixed);
        return this.preferences.move(middle, source, target);
    }
    /** Se a coluna se move - nao e uma das fixas das pontas. */
    isMovable(fields, field, fixed = {}) {
        return split(fields, fixed)[1].includes(field);
    }
    /**
     * Passa a usar outro CONJUNTO de medidas - para a tabela que alterna de conteudo
     * com as mesmas colunas: a data da lista de dias e a hora das leituras sao o mesmo
     * campo, com textos de tamanhos diferentes. A largura que o leitor escolheu vale
     * para todos os conjuntos.
     */
    useMeasureSet(name) {
        if (name === this.measureSet)
            return;
        this.measureSet = name;
        this.markDirty();
    }
    /** O UNIVERSO de colunas: todas que existem, visiveis ou nao. Ordenar usa qualquer uma. */
    setColumns(specs) {
        this.specs = specs;
        this.lastSorted = undefined;
        this.engine.setOptions((previous) => ({ ...previous, columns: this.definitions() }));
        this.configure();
        this.markDirty();
    }
    /** As colunas que aparecem agora, na ordem em que aparecem. */
    setVisible(fields) {
        this.visible = [...fields];
        this.markDirty();
    }
    /** A ordenacao do endereco: `campo` ou `-campo`. Campo desconhecido nao ordena. */
    setSort(sort) {
        const descending = !!sort?.startsWith('-');
        const field = descending ? sort.slice(1) : sort;
        this.sorting =
            field && this.specs.some((spec) => spec.field === field && spec.sortValue)
                ? { field, descending }
                : undefined;
    }
    /**
     * As linhas na ordem pedida - na hora, e nao no redesenho: o presenter precisa delas
     * para montar a pagina. Devolve uma copia: ordenar no lugar mexeria no cache de quem
     * chama. As mesmas linhas (a mesma lista) na mesma ordem pedida saem da ultima conta:
     * trocar de pagina nao reordena dois mil colaboradores.
     */
    sortRows(rows) {
        if (!this.sorting) {
            return [...rows];
        }
        const key = `${this.sorting.descending ? '-' : ''}${this.sorting.field}`;
        if (this.lastSorted?.rows !== rows || this.lastSorted.key !== key) {
            this.configure();
            this.engine.setOptions((previous) => ({ ...previous, data: rows }));
            const result = this.engine.getSortedRowModel().rows.map((row) => row.original);
            this.lastSorted = { rows, key, result };
        }
        return [...this.lastSorted.result];
    }
    /**
     * Publica a geometria, se ha o que publicar. Quem chama e o `onBeforeScopeUpdate` do
     * host; um teste sem presenter chama direto.
     */
    flush() {
        if (!this.dirty) {
            return;
        }
        this.dirty = false;
        this.configure();
        this.publish();
    }
    /** Ha o que publicar: pede o redesenho, e a publicacao vem antes dele. */
    markDirty() {
        this.dirty = true;
        this.host?.update(this.layout);
    }
    /** O estado do TanStack, a partir dos campos deste modelo. As definicoes de coluna nao mudam aqui. */
    configure() {
        const visible = new Set(this.visible);
        const order = [...this.visible, ...this.specs.map((spec) => spec.field).filter((field) => !visible.has(field))];
        const state = {
            columnOrder: order,
            columnVisibility: Object.fromEntries(this.specs.map((spec) => [spec.field, visible.has(spec.field)])),
            columnPinning: { left: this.visible.slice(0, this.pinStart), right: [] },
            columnSizing: this.sizes(),
            sorting: this.sorting ? [{ id: this.sorting.field, desc: this.sorting.descending }] : []
        };
        this.engine.setOptions((previous) => ({
            ...previous,
            state: { ...this.engine.initialState, ...state }
        }));
    }
    definitions() {
        const descending = () => this.sorting?.descending ?? false;
        return this.specs.map((spec) => ({
            id: spec.field,
            // `null`, e nunca `undefined`: o TanStack trata `undefined` antes da nossa comparacao.
            accessorFn: spec.sortValue ? (row) => spec.sortValue(row) ?? null : undefined,
            enableSorting: !!spec.sortValue,
            sortUndefined: false,
            enableResizing: spec.resizable !== false,
            minSize: COLUMN_WIDTH.min,
            maxSize: COLUMN_WIDTH.max,
            sortingFn: (left, right, id) => compareValues(left.getValue(id), right.getValue(id), descending())
        }));
    }
    /** A largura de cada coluna visivel que ja se sabe: a escolhida, ou a medida. */
    sizes() {
        const sizes = {};
        for (const field of this.visible) {
            const width = this.preferences.widthOf(field) ?? this.measure.natural[field];
            if (width !== undefined)
                sizes[field] = width;
        }
        return sizes;
    }
    /**
     * Escreve a geometria no scope - so o que MUDOU. Atribuir um objeto novo com o
     * mesmo conteudo faria o `@observe` avisar a view a toa, e ela recalcularia os
     * estilos de todas as colunas.
     */
    publish() {
        const sizes = this.sizes();
        // Onde as presas encostam so se sabe com a largura de todas elas.
        const pinned = this.visible.slice(0, this.pinStart);
        const known = pinned.every((field) => sizes[field] !== undefined);
        const offsets = {};
        if (known) {
            for (const field of pinned) {
                offsets[field] = this.engine.getColumn(field)?.getStart('left') ?? 0;
            }
        }
        const widths = {};
        const resizable = {};
        for (const field of this.visible) {
            const column = this.engine.getColumn(field);
            if (sizes[field] !== undefined && column)
                widths[field] = column.getSize();
            resizable[field] = column?.getCanResize() ?? false;
        }
        const layout = this.layout;
        if (!sameRecord(layout.widths, widths))
            layout.widths = widths;
        if (!sameRecord(layout.offsets, offsets))
            layout.offsets = offsets;
        if (!sameRecord(layout.resizable, resizable))
            layout.resizable = resizable;
        layout.pinned = pinned.length;
        layout.pinnedWidth = known ? this.engine.getLeftTotalSize() : 0;
        layout.measuredWithRows = this.measure.withRows;
    }
}
function split(fields, fixed) {
    const start = fixed.start ?? 0;
    const end = fields.length - (fixed.end ?? 0);
    return [fields.slice(0, start), fields.slice(start, end), fields.slice(end)];
}
/** O mesmo conteudo, e na mesma ordem de chaves - a ordem das colunas tambem e geometria. */
function sameRecord(left, right) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    return (leftKeys.length === rightKeys.length &&
        leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key]));
}
/**
 * Vazio sempre por ULTIMO, nas duas direcoes - e o que o painel antigo faz.
 *
 * O `sortUndefined: 'last'` do TanStack faria isso, mas na 8.21 ele diz que dois vazios
 * sao diferentes (devolve 1 para os dois lados), e as linhas sem valor sairiam numa
 * ordem qualquer em vez da ordem em que chegaram. Por isso o vazio e tratado aqui, ja
 * compensando a inversao que o TanStack aplica na ordem decrescente.
 */
export function compareValues(left, right, descending) {
    const leftEmpty = left === null || left === undefined;
    const rightEmpty = right === null || right === undefined;
    if (leftEmpty && rightEmpty)
        return 0;
    const last = descending ? -1 : 1;
    if (leftEmpty)
        return last;
    if (rightEmpty)
        return -last;
    if (typeof left === 'number' && typeof right === 'number') {
        return left - right;
    }
    return String(left).localeCompare(String(right), 'pt-BR');
}
//# sourceMappingURL=table-model.js.map